import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Calendar as CalendarIcon,
  MapPin,
  Users,
  Clock,
  FileText,
  Activity,
  ArrowRight,
  Shield,
  Plane,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Radio,
  ExternalLink,
  Compass
} from "lucide-react";
import { Card } from "../../components/ui/card";
import Loader from "./Loader";
import tripsApi from "../api/tripsApi";
import { useAuth } from "../context/AuthContext";

// In-memory module cache for instant (0ms) page switching
let cachedDashboardData = null;

// Universal helper to normalize dates into YYYY-MM-DD
function normalizeDateToYMD(d) {
  if (!d) return null;
  if (typeof d === "string") {
    if (d.includes("/")) {
      const parts = d.split("/");
      if (parts.length === 3) {
        // DD/MM/YYYY
        return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
      }
    }
    return d.split("T")[0];
  }
  if (d instanceof Date) {
    return d.toISOString().split("T")[0];
  }
  return null;
}

// Compute trip start and end date range safely
function getTripRange(trip) {
  const start = normalizeDateToYMD(trip.startDate);
  if (!start) return null;
  let end = normalizeDateToYMD(trip.endDate);
  if (!end) {
    const days = trip.totalDays || (Array.isArray(trip.days) ? trip.days.length : 1);
    const startDateObj = new Date(start);
    startDateObj.setDate(startDateObj.getDate() + Math.max(0, days - 1));
    end = startDateObj.toISOString().split("T")[0];
  }
  return { start, end };
}

export default function DashboardOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Instant render if cached, otherwise load initially
  const [loading, setLoading] = useState(!cachedDashboardData);
  const [dashboardData, setDashboardData] = useState(
    cachedDashboardData || {
      upcoming: [],
      drafts: [],
      shared: [],
      recentActivity: [],
    }
  );

  // Calendar State: navigation month & selected day
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  // Background fetch (Stale-While-Revalidate pattern)
  useEffect(() => {
    let isMounted = true;
    const loadDashboard = async () => {
      try {
        const data = await tripsApi.getDashboard();
        if (isMounted && data) {
          cachedDashboardData = data;
          setDashboardData({
            upcoming: data.upcoming || [],
            drafts: data.drafts || [],
            shared: data.shared || [],
            recentActivity: data.recentActivity || [],
          });
        }
      } catch (err) {
        console.error("Failed to load dashboard from API:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadDashboard();
    return () => {
      isMounted = false;
    };
  }, []);

  // 100% Genuine Data from Backend API
  const upcomingTrips = dashboardData.upcoming || [];
  const draftTrips = dashboardData.drafts || [];
  const sharedTrips = dashboardData.shared || [];
  const recentActivities = dashboardData.recentActivity || [];

  // Combine active trips (upcoming + shared) with computed date ranges
  const mappedTrips = useMemo(() => {
    const combined = [...upcomingTrips, ...sharedTrips];
    // Deduplicate by id
    const uniqueMap = new Map();
    combined.forEach((t) => {
      if (t && t.id && !uniqueMap.has(t.id)) {
        const range = getTripRange(t);
        if (range) {
          uniqueMap.set(t.id, { ...t, range });
        }
      }
    });
    return Array.from(uniqueMap.values());
  }, [upcomingTrips, sharedTrips]);

  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Detect currently LIVE trip (today is between start and end date)
  const liveTrip = useMemo(() => {
    return mappedTrips.find(
      (t) => t.range && todayStr >= t.range.start && todayStr <= t.range.end
    );
  }, [mappedTrips, todayStr]);

  // Calendar calculations
  const calYear = calendarDate.getFullYear();
  const calMonth = calendarDate.getMonth(); // 0-indexed
  const monthName = calendarDate.toLocaleString("default", { month: "long" });

  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(calYear, calMonth, 1).getDay(); // 0 is Sunday

  // Helper to find trips active on a specific YYYY-MM-DD
  const getTripsForDate = (dateStr) => {
    return mappedTrips.filter((t) => {
      return dateStr >= t.range.start && dateStr <= t.range.end;
    });
  };

  const tripsOnSelectedDate = useMemo(() => {
    return getTripsForDate(selectedDate);
  }, [selectedDate, mappedTrips]);

  const handlePrevMonth = () => {
    setCalendarDate(new Date(calYear, calMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarDate(new Date(calYear, calMonth + 1, 1));
  };

  const handleJumpToToday = () => {
    const now = new Date();
    setCalendarDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(todayStr);
  };

  if (loading) {
    return <Loader text="Syncing Your Journeys" />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto space-y-10">
      {/* ── 1. Top Header: Welcome Back + Create Trip Action ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-r from-teal-50/80 via-emerald-50/50 to-cyan-50/80 p-6 sm:p-8 rounded-[2rem] border border-teal-100 shadow-sm">
        <div>
          <span className="px-3 py-1 bg-[#2A9D8F]/15 text-[#2A9D8F] rounded-full text-xs font-bold uppercase tracking-wider">
            Travel Dashboard
          </span>
          <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mt-2">
            Welcome back, {user?.name || "Explorer"} 👋
          </h1>
          <p className="text-gray-500 text-sm sm:text-base mt-1">
            Ready for your next journey? Plan with AI or craft your custom route.
          </p>
        </div>

        <button
          onClick={() => navigate("/dashboard/create-trip")}
          className="flex items-center justify-center gap-2.5 px-6 py-4 bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1b645c] text-white text-sm sm:text-base font-bold rounded-2xl shadow-lg shadow-[#2A9D8F]/30 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <Plus className="w-5 h-5" />
          <span>Create New Trip</span>
        </button>
      </div>

      {/* ── 2. LIVE TRIP BANNER (Rendered if user is currently on an active expedition today) ── */}
      {liveTrip && (
        <div className="p-5 sm:p-6 bg-gradient-to-r from-emerald-600 via-teal-600 to-[#2A9D8F] text-white rounded-3xl shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4 border border-emerald-400/40 relative overflow-hidden">
          <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
          <div className="space-y-1.5 z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-black uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-red-400 animate-ping"></span>
              <Radio className="w-3.5 h-3.5 text-white animate-pulse" />
              <span>Expedition Live Today</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white">
              {liveTrip.title} &mdash; {liveTrip.destination}
            </h2>
            <p className="text-xs sm:text-sm text-emerald-100 flex items-center gap-2">
              <CalendarIcon className="w-3.5 h-3.5" />
              <span>
                {liveTrip.range?.start} &rarr; {liveTrip.range?.end}
              </span>
              <span>&bull;</span>
              <span>{liveTrip.travelers || 1} Travelers</span>
            </p>
          </div>

          <button
            onClick={() => navigate(`/dashboard/trip/${liveTrip.id}`)}
            className="z-10 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-emerald-800 font-extrabold text-sm rounded-2xl shadow-md hover:bg-emerald-50 hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
          >
            <span>Open Live Expedition</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── 3. INTERACTIVE EXPEDITION CALENDAR & SCHEDULE HUB ── */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2.5">
              <CalendarIcon className="w-6 h-6 text-[#2A9D8F]" />
              <span>Expedition Calendar & Schedules</span>
            </h2>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5">
              Dates highlighted below represent your scheduled and active live expeditions.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleJumpToToday}
              className="px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-[#2A9D8F] text-xs font-bold rounded-xl border border-teal-200 transition-colors cursor-pointer"
            >
              Today
            </button>
            <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs">
              <button
                onClick={handlePrevMonth}
                aria-label="Previous month"
                className="p-2 hover:bg-gray-50 text-gray-600 transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 text-xs font-bold text-gray-800 whitespace-nowrap">
                {monthName} {calYear}
              </span>
              <button
                onClick={handleNextMonth}
                aria-label="Next month"
                className="p-2 hover:bg-gray-50 text-gray-600 transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* 2-Column Calendar Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left 7 Columns: Calendar Month Grid */}
          <Card className="lg:col-span-7 p-5 sm:p-6 bg-white rounded-3xl border border-gray-100 shadow-sm space-y-4">
            {/* Weekdays header */}
            <div className="grid grid-cols-7 text-center">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((dayName) => (
                <div key={dayName} className="text-xs font-bold uppercase tracking-wider text-gray-400 py-1.5">
                  {dayName}
                </div>
              ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
              {/* Blank leading slots */}
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div key={`blank-${i}`} className="min-h-[50px] sm:min-h-[64px] rounded-2xl bg-gray-50/40 opacity-40"></div>
              ))}

              {/* Days of current month */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const currentCellDate = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
                const isToday = currentCellDate === todayStr;
                const isSelected = currentCellDate === selectedDate;
                const activeTripsForDay = getTripsForDate(currentCellDate);
                const hasTrip = activeTripsForDay.length > 0;
                const hasLiveTripToday = isToday && hasTrip;

                return (
                  <button
                    key={dayNum}
                    type="button"
                    onClick={() => setSelectedDate(currentCellDate)}
                    className={`min-h-[50px] sm:min-h-[64px] p-1.5 sm:p-2 rounded-2xl border text-left flex flex-col justify-between transition-all cursor-pointer relative group ${
                      isSelected
                        ? "border-[#2A9D8F] bg-teal-50/70 shadow-sm ring-2 ring-[#2A9D8F]/30"
                        : hasLiveTripToday
                        ? "border-emerald-400 bg-emerald-50/80 shadow-xs"
                        : hasTrip
                        ? "border-teal-200 bg-teal-50/30 hover:border-[#2A9D8F]/60"
                        : "border-gray-100 bg-white hover:bg-gray-50 hover:border-gray-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs sm:text-sm font-bold leading-none ${
                          isToday
                            ? "w-6 h-6 flex items-center justify-center rounded-full bg-[#2A9D8F] text-white"
                            : isSelected
                            ? "text-[#2A9D8F]"
                            : "text-gray-800"
                        }`}
                      >
                        {dayNum}
                      </span>

                      {/* Live or Expedition Marker */}
                      {hasLiveTripToday ? (
                        <span className="flex h-2 w-2 relative">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                        </span>
                      ) : hasTrip ? (
                        <span className="w-2 h-2 rounded-full bg-[#2A9D8F]"></span>
                      ) : null}
                    </div>

                    {/* Trip summary pill */}
                    {hasTrip && (
                      <div className="mt-1">
                        <span
                          className={`block text-[10px] font-bold truncate rounded-md px-1 py-0.5 ${
                            hasLiveTripToday
                              ? "bg-red-100 text-red-700"
                              : "bg-teal-100/80 text-teal-800"
                          }`}
                        >
                          {hasLiveTripToday ? "Live Now" : activeTripsForDay[0]?.destination || "Trip"}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Calendar Legend */}
            <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-100 text-[11px] text-gray-500 font-medium">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                <span>Live Now (Active Today)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#2A9D8F]"></span>
                <span>Scheduled Expedition</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full border-2 border-[#2A9D8F]"></span>
                <span>Selected Date</span>
              </div>
            </div>
          </Card>

          {/* Right 5 Columns: Selected Date Expedition Inspector */}
          <Card className="lg:col-span-5 p-5 sm:p-6 bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Compass className="w-5 h-5 text-[#2A9D8F]" />
                  <h3 className="font-extrabold text-gray-900 text-base">
                    Date Details &bull;{" "}
                    <span className="text-[#2A9D8F]">
                      {new Date(selectedDate).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </h3>
                </div>
                {selectedDate === todayStr && (
                  <span className="px-2.5 py-0.5 bg-teal-50 text-[#2A9D8F] text-[11px] font-bold rounded-full border border-teal-200">
                    Today
                  </span>
                )}
              </div>

              {tripsOnSelectedDate.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                    {tripsOnSelectedDate.length} Active Expedition{tripsOnSelectedDate.length > 1 ? "s" : ""}:
                  </p>

                  {tripsOnSelectedDate.map((trip) => {
                    const isTripLiveToday = todayStr >= trip.range?.start && todayStr <= trip.range?.end;

                    return (
                      <div
                        key={trip.id}
                        onClick={() => navigate(`/dashboard/trip/${trip.id}`)}
                        className="p-4 rounded-2xl border border-teal-100 bg-teal-50/30 hover:bg-teal-50/70 transition-all cursor-pointer space-y-3 group"
                      >
                        <div className="flex items-start gap-3">
                          <img
                            src={
                              trip.coverImageUrl ||
                              trip.image ||
                              "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800"
                            }
                            alt={trip.title}
                            className="w-16 h-16 rounded-xl object-cover shrink-0 border border-gray-200 shadow-xs"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {isTripLiveToday ? (
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-black rounded-md uppercase tracking-wider">
                                  Live Now
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-teal-100 text-teal-800 text-[10px] font-bold rounded-md uppercase tracking-wider">
                                  Scheduled
                                </span>
                              )}
                              <span className="text-xs text-gray-500 font-medium truncate">
                                {trip.destination}
                              </span>
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mt-1 truncate group-hover:text-[#2A9D8F] transition-colors">
                              {trip.title}
                            </h4>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                              {trip.range?.start} &rarr; {trip.range?.end}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-teal-100/60 text-xs">
                          <span className="font-bold text-gray-900">
                            ₹{parseFloat(trip.budget || 0).toLocaleString()}
                          </span>
                          <span className="text-[#2A9D8F] font-bold group-hover:underline flex items-center gap-1">
                            <span>View Full Expedition</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center space-y-3">
                  <div className="w-12 h-12 rounded-2xl bg-gray-50 text-gray-400 flex items-center justify-center mx-auto border border-gray-100">
                    <CalendarIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm">No Expeditions Scheduled</h4>
                    <p className="text-xs text-gray-400 mt-1 max-w-[240px] mx-auto">
                      You have no journeys active on this date. Perfect time to plan your next getaway!
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/dashboard/create-trip?startDate=${selectedDate}`)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#2A9D8F] hover:bg-[#238177] text-white text-xs font-bold rounded-xl shadow-xs transition-colors cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Plan Trip for this Date</span>
                  </button>
                </div>
              )}
            </div>

            {/* Bottom quick stats */}
            <div className="pt-4 border-t border-gray-100 grid grid-cols-2 gap-2 text-center text-xs">
              <div className="p-2.5 rounded-xl bg-gray-50 border border-gray-100">
                <span className="block font-black text-gray-900 text-sm">{upcomingTrips.length}</span>
                <span className="text-[11px] text-gray-400">Total Expeditions</span>
              </div>
              <div className="p-2.5 rounded-xl bg-gray-50 border border-gray-100">
                <span className="block font-black text-gray-900 text-sm">{draftTrips.length}</span>
                <span className="text-[11px] text-gray-400">Draft Itineraries</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* ── 4. Your Trips Header & Main Sections ── */}
      <div className="space-y-10">
        <div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Your Trips</h2>
          <div className="w-full h-px bg-gray-200 mt-3"></div>
        </div>

        {/* ── Subsection A: Upcoming Trips ── */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="w-2.5 h-2.5 bg-teal-500 rounded-full"></span>
              <span>Upcoming</span>
              <span className="text-xs text-gray-400 font-semibold">({upcomingTrips.length})</span>
            </h3>
          </div>

          {upcomingTrips.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {upcomingTrips.map((trip) => (
                <Card
                  key={trip.id}
                  onClick={() => navigate(`/dashboard/trip/${trip.id}?type=upcoming`)}
                  className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group flex flex-col justify-between cursor-pointer"
                >
                  <div>
                    <div className="relative h-48 overflow-hidden bg-slate-100">
                      <img
                        src={trip.coverImageUrl || trip.image || "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800"}
                        alt={trip.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 right-3 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-xs font-semibold">
                        Upcoming
                      </div>
                      <div className="absolute bottom-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-md rounded-full text-gray-800 text-xs font-bold shadow-sm flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-[#2A9D8F]" />
                        <span>{trip.destination}</span>
                      </div>
                    </div>

                    <div className="p-5 space-y-2">
                      <h4 className="font-bold text-gray-900 text-base group-hover:text-[#2A9D8F] transition-colors line-clamp-1">
                        {trip.title}
                      </h4>
                      <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                        <span>{trip.startDate ? `${trip.startDate} - ${trip.endDate || ''}` : `${trip.totalDays} Days`}</span>
                      </p>
                    </div>
                  </div>

                  <div className="p-5 pt-0">
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <Users className="w-3.5 h-3.5 text-teal-600" />
                        <span>{trip.travelers || 2} travelers</span>
                      </div>
                      <span className="font-bold text-gray-900 text-sm">
                        ₹{parseFloat(trip.budget || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 border border-gray-100 text-center space-y-3">
              <MapPin className="w-8 h-8 text-teal-500 mx-auto opacity-40" />
              <p className="text-gray-500 text-sm font-medium">No upcoming journeys yet. Ready for your next adventure?</p>
              <button
                onClick={() => navigate('/dashboard/create-trip')}
                className="px-4 py-2 bg-teal-50 text-[#2A9D8F] font-bold text-xs rounded-xl hover:bg-teal-100 transition-colors cursor-pointer"
              >
                Plan a Trip
              </button>
            </div>
          )}
        </section>

        {/* ── Subsection B: Drafts ── */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <span className="w-2.5 h-2.5 bg-amber-500 rounded-full"></span>
              <span>Drafts</span>
              <span className="text-xs text-gray-400 font-semibold">({draftTrips.length})</span>
            </h3>
          </div>

          {draftTrips.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {draftTrips.map((draft) => (
                <Card
                  key={draft.id}
                  onClick={() => navigate(`/dashboard/create-trip?draftId=${draft.id}`)}
                  className="bg-white rounded-3xl overflow-hidden border border-amber-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group flex flex-col justify-between cursor-pointer"
                >
                  <div>
                    <div className="relative h-40 overflow-hidden bg-slate-100">
                      <img
                        src={draft.coverImageUrl || "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=800"}
                        alt={draft.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 right-3 px-3 py-1 bg-amber-500 text-white rounded-full text-xs font-semibold shadow-sm flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        <span>Draft</span>
                      </div>
                    </div>

                    <div className="p-5 space-y-2">
                      <h4 className="font-bold text-gray-900 text-base group-hover:text-amber-600 transition-colors line-clamp-1">
                        {draft.title}
                      </h4>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                        <span>{draft.destination}</span> &bull; <span>{draft.totalDays} Days</span>
                      </p>
                    </div>
                  </div>

                  <div className="p-5 pt-0">
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs">
                      <span className="text-gray-400 font-medium">{draft.updatedAt ? new Date(draft.updatedAt).toLocaleDateString() : "Draft saved"}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/create-trip?draftId=${draft.id}`);
                        }}
                        className="text-amber-600 font-bold hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <span>Continue</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 border border-gray-100 text-center space-y-3">
              <FileText className="w-8 h-8 text-amber-500 mx-auto opacity-40" />
              <p className="text-gray-500 text-sm font-medium">No drafts in progress. Plan an itinerary with AI or craft your own route.</p>
              <button
                onClick={() => navigate('/dashboard/create-trip')}
                className="px-4 py-2 bg-amber-50 text-amber-700 font-bold text-xs rounded-xl hover:bg-amber-100 transition-colors cursor-pointer"
              >
                Start a Draft
              </button>
            </div>
          )}
        </section>

        {/* ── 5. Recent Activity Section ── */}
        <section className="space-y-4 pt-4">
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
              <Activity className="w-6 h-6 text-[#2A9D8F]" />
              <span>Recent Activity</span>
            </h2>
            <div className="w-full h-px bg-gray-200 mt-3"></div>
          </div>

          <Card className="p-6 bg-white rounded-3xl border border-gray-100 shadow-sm divide-y divide-gray-100">
            {recentActivities.length > 0 ? (
              recentActivities.map((act, idx) => (
                <div key={idx} className="py-3.5 first:pt-0 last:pb-0 flex items-start gap-4 text-sm">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#2A9D8F] mt-2 shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{act.title}</p>
                    {act.description && (
                      <p className="text-gray-500 text-xs mt-0.5">{act.description}</p>
                    )}
                  </div>
                  <span className="text-gray-400 text-xs shrink-0 whitespace-nowrap">
                    {act.time || (act.createdAt ? new Date(act.createdAt).toLocaleDateString() : "recently")}
                  </span>
                </div>
              ))
            ) : (
              <div className="py-6 text-center text-sm text-gray-400">
                No recent activity yet. Collaborative edits and invite acceptances will appear here.
              </div>
            )}
          </Card>
        </section>
      </div>
    </div>
  );
}
